import { dropdownViewGet, duplicate } from '../service/api-service';
import { Dropdown } from 'primereact/dropdown';
import { FormControl, FormLabel } from '@mui/joy';
import { AlternateEmailOutlined } from '@mui/icons-material';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useState } from 'react';

interface inputPrams {
  label: string,
  type: string,
  email: string,
  setEmail: React.Dispatch<string>,
  required?: boolean,
  validationMessage?: string,
  setValidationMessage?: React.Dispatch<string>,
  validationStatus?: 'success' | 'warning' | 'error' | '',
  setValidationStatus?: React.Dispatch<'success' | 'warning' | 'error' | ''>
  loading: boolean,
  setLoading: React.Dispatch<boolean>,
}

export default function EmailValidationApi({
  label, type, email, setEmail, required, validationMessage, setValidationMessage, validationStatus, setValidationStatus, loading, setLoading
}: inputPrams) {

  const getValidationStyles = (status: string) => {
    switch (status) {
      case 'success':
        return {
          color: "#2e7d32",
          backgroundColor: "#e8f5e8",
          border: "1px solid #4caf50",
          icon: "✅"
        };
      case 'warning':
        return {
          color: "#e65100",
          backgroundColor: "#fff3e0",
          border: "1px solid #ffcc02",
          icon: "⚠️"
        };
      case 'error':
        return {
          color: "#d32f2f",
          backgroundColor: "#ffebee",
          border: "1px solid #f44336",
          icon: "❌"
        };
      default:
        return {
          color: "#666",
          backgroundColor: "#f5f5f5",
          border: "1px solid #ddd",
          icon: "ℹ️"
        };
    }
  };

  const getValidationColor = (status: string) => {
    switch (status) {
      case 'success': return '#2dd36f';
      case 'warning': return '#ffc409';
      case 'error': return '#eb445a';
      default: return '';
    }
  };

  async function validateEmail() {
    
    // Email format validation
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email || !emailRegex.test(email)) {
      if(setValidationStatus) setValidationStatus('warning');
      if(setValidationMessage) setValidationMessage("Please enter a valid email address in the correct format (e.g., user@example.com)");
      return;
    }
    
    setLoading(true);
    try {
      const isDuplicate = await duplicate({
        type: type, email: email
      });
      setLoading(false);
      
      if (isDuplicate) {
        if(setValidationStatus) setValidationStatus('error');
        if(setValidationMessage) setValidationMessage("This email address is already in use. Please note email addresses must be unique.");
        return;
      }
      
      if(setValidationStatus) setValidationStatus('success');
      if(setValidationMessage) setValidationMessage("Email address is available and ready to use");
    } catch (error) {
      setLoading(false);
      if(setValidationStatus) setValidationStatus('error');
      if(setValidationMessage) setValidationMessage("Unable to validate email address. Please check your connection and try again");
    }
  }

  return (
    <FormControl
      sx={{ flexGrow: 1 }}
    >
      <FormLabel>{label}{required ? <div style={{ color: 'red' }}>*</div> : ""}</FormLabel>
      <div className="p-inputgroup ">
        <span className="p-inputgroup-addon PrimeSmallInput" style={{ 
          borderColor: getValidationColor(validationStatus || ''),
          borderWidth: validationStatus ? '2px' : '1px'
        }}>
          <AlternateEmailOutlined />
        </span>
        <InputText 
          style={{ 
            width: '100%', 
            borderColor: getValidationColor(validationStatus || ''),
            borderWidth: validationStatus ? '2px' : '1px'
          }} 
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          onBlur={e => validateEmail()} 
          type="text" 
          className="p-inputtext-sm PrimeSmallInput" 
          placeholder="Please enter email address" 
        />
        {
          loading ? <span className="p-inputgroup-addon PrimeSmallInput" style={{ 
            borderColor: getValidationColor(validationStatus || ''),
            borderWidth: validationStatus ? '2px' : '1px'
          }}>
            <ProgressSpinner style={{ width: '70%', height: '100%' }} />
          </span> : ""
        }
      </div>
      {validationMessage && (
        <div 
          style={{ 
            fontSize: "small",
            color: getValidationStyles(validationStatus || '').color,
            backgroundColor: getValidationStyles(validationStatus || '').backgroundColor,
            border: getValidationStyles(validationStatus || '').border,
            borderRadius: "4px",
            padding: "8px 12px",
            marginTop: "8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ fontSize: "14px" }}>{getValidationStyles(validationStatus || '').icon}</span>
          <span>
            {validationMessage}
          </span>
        </div>
      )}
    </FormControl>
  );
}